import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { CertificateRecordService } from 'app/library/certificate-record/services/certificate-record.service';
import { ReplaceCertificateDialogComponent } from 'app/library/certification-provider/components/dialogs/replace-certificate-dialog/replace-certificate-dialog.component';
import { CertificationFlatNode } from 'app/library/certification-provider/components/providers-all-list/providers-all-list.component';
import { CertificationProvider } from 'app/library/certification-provider/models/certification-provider';
import { CertificationProviderService } from 'app/library/certification-provider/services/certification-provider.service';
import { Certification } from 'app/library/certifications/models/certification';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import * as moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-add-certificate-user-profile-dialog',
    templateUrl: './add-certificate-user-profile-dialog.component.html',
    styleUrls: ['./add-certificate-user-profile-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AddCertificateUserProfileDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    title: string;

    @Input()
    userId?: string;

    @Input()
    providerId: string;

    @Input()
    certificationId: string;

    @Input()
    certificate: CertificationFlatNode;

    @Input()
    minIssueDate: moment.Moment;

    @Output()
    onReplaced = new EventEmitter<boolean>();

    isToDoFeatureEnabled: boolean = false;
    selectedCertificationProviderId: string;
    selectedCertificationId: string;
    selectedCertification: Certification;
    certificationForm: FormGroup;
    uploadedCertificateFiles: File[] = [];
    isSaveButtonLoading: boolean = false;
    minDate: Date;
    Validators = Validators;
    hasEndDate = new FormControl(false);
    isDragOver = false;
    isLoading: boolean = false;
    profile: User;
    createSilently = new FormControl(false);

    constructor(
        public dialogRef: MatDialogRef<AddCertificateUserProfileDialogComponent>,
        private _certificationProviderService: CertificationProviderService,
        private _certificateRecordService: CertificateRecordService,
        private _featureService: FeatureService,
        private _fb: FormBuilder,
        private _matDialog: MatDialog,
        private _store: Store
    ) {
        super();
    }

    ngOnInit(): void {
        this.isToDoFeatureEnabled = this._featureService.checkFeatureStatus(FeatureSwitchName.ToDo);
        this.profile = this._store.selectSnapshot(UserState.getMyInfo);

        this.certificationForm = this._fb.group({
            provider: [this.providerId || null, Validators.required],
            certification: [{ value: this.certificationId || null, disabled: !this.providerId }, Validators.required],
            issueDate: [null, Validators.required],
            document: [null],
        });

        this.updateExpirationDate();
    }

    updateExpirationDate(): void {
        const startDate = this.certificationForm.get('issueDate').value;

        this.minDate = startDate ? new Date(startDate) : new Date();
    }

    onRemoveAddedCertificate() {
        this.uploadedCertificateFiles = [];
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;
    }

    onFileDropped(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;

        if (event.dataTransfer && event.dataTransfer.files.length > 0) {
            const files: File[] = Array.from(event.dataTransfer.files);
            this.handleFiles(files);
        }
    }

    handleFiles(files: File[]): void {
        const fileSubject = new BehaviorSubject<File | null>(null);
        const collectedFiles: File[] = [];

        fileSubject
            .asObservable()
            .pipe(
                filter((file) => !!file),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((file) => {
                collectedFiles.push(file!);
                if (collectedFiles.length === files.length) {
                    fileSubject.complete();
                    this.fileChange(collectedFiles);
                }
            });

        files.forEach((file) => fileSubject.next(file));
    }

    fileChange(files: FileList | File[]): void {
        if (files instanceof FileList) {
            const temp: File[] = [];
            for (const key of Object.keys(files)) {
                temp[key] = files[key];
            }
            files = temp;
        }

        this.uploadedCertificateFiles = files;
    }

    onProviderSelected(provider: CertificationProvider): void {
        this.selectedCertificationProviderId = provider.id;
        this.selectedCertificationId = null;
        this.selectedCertification = null;
        this.hasEndDate.setValue(false);
        this.certificationForm.removeControl('expirationDate');
        if (this.isToDoFeatureEnabled) {
            this.certificationForm.removeControl('notifyAboutExpiration');
        }
    }

    onCertificationSelected(certification: Certification): void {
        this.selectedCertificationId = certification.id;
        this.selectedCertification = certification;

        if (this.selectedCertification.certificationName === 'other') {
            this.hasEndDate.setValue(true);
        } else {
            this.hasEndDate.setValue(false);
        }

        if (this.selectedCertification.hasExpirationDate || this.selectedCertification.certificationName === 'other') {
            if (!this.certificationForm.get('expirationDate')) {
                this.certificationForm.addControl('expirationDate', new FormControl(null, Validators.required));
            }
            if (this.isToDoFeatureEnabled && !this.certificationForm.get('notifyAboutExpiration')) {
                this.certificationForm.addControl('notifyAboutExpiration', new FormControl(true));
            }
        } else {
            this.certificationForm.removeControl('expirationDate');
            if (this.isToDoFeatureEnabled) {
                this.certificationForm.removeControl('notifyAboutExpiration');
            }
        }
    }

    onChangeEndDateToggle(event: MatCheckboxChange): void {
        if (event.checked) {
            this.certificationForm.addControl('expirationDate', new FormControl(null, Validators.required));
            if (this.isToDoFeatureEnabled) {
                this.certificationForm.addControl('notifyAboutExpiration', new FormControl(false));
            }
        } else {
            this.certificationForm.removeControl('expirationDate');
            if (this.isToDoFeatureEnabled) {
                this.certificationForm.removeControl('notifyAboutExpiration');
            }
        }
    }

    openReplaceDialog(): void {
        const dialogRef = this._matDialog.open(ReplaceCertificateDialogComponent, {
            width: '976px',
        });

        this.dialogRef.close();

        dialogRef.componentInstance.certificateToBeReplaced = this.certificate;
        dialogRef.componentInstance.userId = this.userId;

        dialogRef.afterClosed().subscribe(async (result: { newSelectedCertificate: CertificationFlatNode }) => {
            if (result) {
                const success = await this._certificationProviderService.replaceCertificate(this.certificate.id, result.newSelectedCertificate.id);

                if (success) {
                    this.onReplaced.emit(true);
                }
            }
        });
    }

    async saveSelected(): Promise<void> {
        this.isSaveButtonLoading = true;
        let sendNotification = null;

        if (this.userId === this.profile.id) {
            sendNotification = null;
        } else {
            sendNotification = !this.createSilently.value || false;
        }

        const certificateRecord = await this._certificateRecordService.addCertificateRecord(
            this.selectedCertificationId,
            this.certificationForm.get('issueDate').value,
            this.certificationForm.get('expirationDate')?.value,
            this.userId,
            this.uploadedCertificateFiles[0],
            this.certificationForm.get('notifyAboutExpiration')?.value || false,
            sendNotification
        );

        this.isSaveButtonLoading = false;

        if (certificateRecord) {
            this.dialogRef.close(certificateRecord);
        }
    }
}
